import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useRoute } from "@react-navigation/native";
import { Linking, StyleSheet, View } from "react-native";
import Text from 'theme/Text'
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import _ from "lodash";
import { useInterval } from 'utils/hooks/use-interval'
import { dataConstants } from 'services/redux/data/constants'
import useMinute from 'utils/hooks/use-minute'
import ThemedBlock from 'components/atoms/ThemedBlock'
import ThemedQrCode from 'components/atoms/ThemedQrCode'
import { textStyles } from 'styles/text'
import i18n from 'utils/i18n'
import Button from 'theme/Button'
import HelperDate, { dateFormat } from 'utils/helper-date'
import HelperString from 'utils/helper-string'
import HelperValidate from 'utils/helper-validate'
import VStack from 'components/atoms/stack/VStack'
import Title from 'components/atoms/Title'
import Translate from 'utils/translate'
import { bookingConstants } from 'utils/constants/booking-constants'
import HelperArray from 'utils/helper-array'

const HomeQrCodeTimeslotScene = () => {

	const minute = useMinute(); // Keep this hook to render every exact minute
	const route = useRoute();

	const timeslotGuid = _.get( route, [ 'params', 'timeslot' ], null );

	const _employee = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE];
	} )
	const _booking = useSelector( state => {
		return state.data[dataConstants.BOOKINGS][timeslotGuid];
	} )
	const _timeslotKinds = useSelector(state => {
		return state.data[dataConstants.TIMESLOT_KINDS];
	} );
	const _lunchOptions = useSelector( state => {
		return state.data[dataConstants.LUNCH_OPTIONS];
	} );
	
	const [ qrCodeData, setQrCodeData ] = useState(null);
	const [ generatedAt, setGeneratedAt ] = useState( HelperDate.date().format( dateFormat.DATE_TIME ) );

	const qrCodeIsCustom = _.get( _booking, 'scan_qr_code_is_custom', 'no' ) === 'yes';
	
	const createQrCode = () => {
		// This for for a timeslot/booking which has a custom qr code
		// The value is returned from the server
		if( qrCodeIsCustom ) {
			// Check if we have a QR code from the booking itself
			const qrCodeString = _.get( _booking, 'scan_qr_code', null );

			if( qrCodeString !== null ) {
				setQrCodeData( qrCodeString );
			}

			return;
		}

		// Otherwise create our own code
		const qrCodeData = {
			created_at: generatedAt,
			action: 'validate_booking',
			employeeBooking: _.get( _booking, 'guid' ),
			employee: _.get( _employee, [ 'guid' ] ),
		};

		setQrCodeData( JSON.stringify( qrCodeData ) );
	}

	useInterval( () => {
		setGeneratedAt( HelperDate.date().format( dateFormat.DATE_TIME ) );
	}, 30 * 1000 );

	useEffect( () => {
		createQrCode();
	}, [ _booking, generatedAt ] );
	
	const timeslotKind = useMemo( () => {
		const timeslotKindIdentifier = _.get( _booking, [ 'timeslot','timeslot_kind','kind' ], null )+'_'+_.get( _booking, [ 'timeslot','timeslot_kind','sub_kind' ], null );
		
		return _.get( _timeslotKinds, [ timeslotKindIdentifier ], null );
	}, [ _timeslotKinds, _booking ] )

	const textView = useMemo( () => {
		if( qrCodeIsCustom && _.isEmpty( qrCodeData ) ) {
			return (
				<ThemedBlock
					shadow>
					
					<Text
						style={ textStyles.textCenter }>
						
						{ i18n.t('page.qr_code.not_available_yet') }
					</Text>
				</ThemedBlock>
			)
		}
		
		return null;
	}, [ qrCodeIsCustom, qrCodeData ] );
	
	const lunchOptions = useMemo( () => {
		const result = [];
		if( _booking?.kind === bookingConstants.LUNCH ) {
			_.forEach( _booking?.lunch_options ?? [], ( option ) => {
				const lunchOption = _.find( _lunchOptions, { guid: option?.lunch_option?.guid } );
				if( lunchOption ) {
					result.push( lunchOption )
				}
			})
		}
		
		return result;
	}, [ _lunchOptions, _booking ])
	
	const lunchOptionsString = useMemo( () => {
		const list = _.map( lunchOptions, ( option ) => {
			return Translate.field(option, 'title', 'name');
		} )
		
		return HelperArray.naturalJoin( list );
	}, [ lunchOptions ])
	
	const instructionsButton = useMemo( () => {
		if( timeslotKind && HelperValidate.isValid( timeslotKind?.instructions_link ) && !HelperString.isEmptyString( timeslotKind?.instructions_link ) ){
			return (
				<Button
					style={ styles.instructionButton }
					onPress={ () => {
						Linking.openURL( timeslotKind.instructions_link )
					}}>
					
					{ i18n.t('page.qr_code.instructions') }
				</Button>
			);
		}
		
		return null;
	}, [ _timeslotKinds, timeslotKind ] )
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>

			<ThemedLayout
				title={ i18n.t('page.qr_code.title') }
				enableScrollView={ true }
				topNavigation={ true }
				showBackButton={ true }>
				
				<View
					style={styles.viewAlignItemsCenter}>
					
					<Title style={styles.titleMarginBottom0}>{ Translate.field( timeslotKind, 'title', 'name' ) }</Title>
					<Text>{ lunchOptionsString }</Text>
				</View>

				{ qrCodeData !== null && (
					<VStack>
						<ThemedBlock
							mediumPadding
							shadow>
							
							<ThemedQrCode
								value={ qrCodeData }
								size={'90%'}
							/>
							
							<View
								style={ styles.bookingDateView }>
								
								<Text>{ _booking?.booking_date }</Text>
							</View>
						</ThemedBlock>
						
						<VStack>
							<Text style={ [ textStyles.textCenter, styles.brightnessText ] }>
								{ i18n.t('basic.brightness_info') }
							</Text>
							
							<Text style={ [ textStyles.textCenter, styles.generatedAtText ] }>
								{ generatedAt }
							</Text>
						</VStack>
					</VStack>
				) }

				<VStack>
					{ textView }
					
					{ instructionsButton }
				</VStack>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	)
}

const styles = StyleSheet.create({
	viewAlignItemsCenter: {
		alignItems: 'center',
	},
	titleMarginBottom0: {
		marginBottom: 0,
	},
	bookingDateView: {
		marginTop: 8,
		alignItems: 'center'
	},
	instructionButton: {
		flex: 0,
		width: 'auto',
		margin: 10
	},
	brightnessText: {
		adding: 0
	},
	generatedAtText: {
		fontSize: 10,
		textAlign: 'center',
		padding: 0
	}
})

export default HomeQrCodeTimeslotScene
